import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaBolt, 
  FaGem, 
  FaBullseye, 
  FaShieldAlt, 
  FaMobile, 
  FaGift,
  FaCalculator,
  FaHandshake,
  FaClock,
  FaChartLine
} from 'react-icons/fa';
import HeroCarousel from '../components/HeroCarousel';
import LoanStatusTracker from '../components/LoanStatusTracker';
import '../components/HeroCarousel.css';

const Home = () => {
  useEffect(() => {
    // Initialize AOS animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right');
    animatedElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="home-page">
      {/* Hero Carousel Section */}
      <HeroCarousel />

      {/* Quick Services Section */}
      <section className="quick-services">
        <div className="content-wrapper">
          <div className="quick-services-grid">
            <Link to="/emi-calculator" className="quick-service-card fade-in">
              <div className="service-icon">
                <FaCalculator />
              </div>
              <h3>Loan Calculator</h3>
              <p>Calculate your EMI instantly</p>
            </Link>
            <Link to="/services" className="quick-service-card fade-in">
              <div className="service-icon">
                <FaHandshake />
              </div>
              <h3>Loan Products</h3>
              <p>Explore our loan offerings</p>
            </Link>
            <Link to="/apply" className="quick-service-card fade-in">
              <div className="service-icon">
                <FaClock />
              </div>
              <h3>Quick Apply</h3>
              <p>Apply in 5 minutes</p>
            </Link>
            {/* <Link to="/contact" className="quick-service-card fade-in">
              <div className="service-icon">
                <FaChartLine />
              </div>
              <h3>Track Status</h3>
              <p>Monitor your application</p>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="features-container">
          <div className="features-header fade-in">
            <h2>Why Choose Loan Made Easy?</h2>
            <p>Experience the perfect blend of technology and trust with our comprehensive loan solutions</p>
          </div>
          
          <div className="features-grid">
            <div className="feature-card slide-in-left">
              <div className="feature-icon">
                <FaBolt />
              </div>
              <h3>Approval within 24hours</h3>
              <p>Get approved within 24 hours with our AI-powered assessment system that evaluates your profile instantly.</p>
            </div>
            
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <FaGem />
              </div>
              <h3>Flexible Interest Rate</h3>
              <p>Enjoy competitive interest rates starting from 7.3% APR with flexible repayment options tailored to your needs.</p>
            </div>
            
            <div className="feature-card slide-in-right">
              <div className="feature-icon">
                <FaBullseye />
              </div>
              <h3>Smart Matching</h3>
              <p>Our advanced AI algorithm finds the perfect loan product with perfect matching of BANK/NBFC based on your profile and requirements.</p>
            </div>
            
            <div className="feature-card slide-in-left">
              <div className="feature-icon">
                <FaShieldAlt />
              </div>
              <h3>Bank-Level Security</h3>
              <p>Your data is protected with 256-bit encryption and multi-layer security protocols ensuring complete safety.</p>
            </div>
            
            <div className="feature-card fade-in">
              <div className="feature-icon">
                <FaMobile />
              </div>
              <h3>Mobile Optimized</h3>
              <p>Apply anywhere, anytime with our responsive design and mobile-first approach for seamless experience.</p>
            </div>
            
            <div className="feature-card slide-in-right">
              <div className="feature-icon">
                <FaGift />
              </div>
              <h3>Zero Commission Fees</h3>
              <p>Transparent pricing with no processing fees, no prepayment penalties, and no hidden charges whatsoever.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Status Tracker Section */}
      <LoanStatusTracker />

      {/* Promotional Interest Rates Section */}
      <section className="promotional-rates">
        <div className="content-wrapper">
          <div className="promotional-header fade-in">
            <h2>Special Promotional Rates</h2>
            <p>
  Limited-time promotional rates for display purposes only. 
  These rates are not used in EMI or loan eligibility calculations.
</p>
          </div>
          
          <div className="rates-grid">
            <div className="rate-card fade-in">
              <div className="loan-type">Loan Against Property</div>
              <div className="rate-value">8.5%</div>
              <p className="rate-label">Promotional Rate</p>
            </div>
            
            <div className="rate-card fade-in">
              <div className="loan-type">Home Loan</div>
              <div className="rate-value">7.5%</div>
              <p className="rate-label">Promotional Rate</p>
            </div>
            
            <div className="rate-card fade-in">
              <div className="loan-type">Personal Loan</div>
              <div className="rate-value">9.5%</div>
              <p className="rate-label">Promotional Rate</p>
            </div>
            
            <div className="rate-card fade-in">
              <div className="loan-type">Business Loan</div>
              <div className="rate-value">11%</div>
              <p className="rate-label">Promotional Rate</p>
            </div>
            
            <div className="rate-card fade-in">
              <div className="loan-type">Car Loan</div>
              <div className="rate-value">9.5%</div>
              <p className="rate-label">Promotional Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lending Partners Section */}
      <section className="lending-partners">
        <div className="content-wrapper">
          <div className="partners-header fade-in">
            <h2>Our Lending Partners</h2>
            <p className="partners-highlight">
              ✓ Top 10 Banks & Top 10 NBFCs
            </p>
            <p className="partners-subtitle">We charge zero commission fees for loan process</p>
          </div>

          {/* Banks Section */}
          <div className="partners-category">
            <h3 className="category-title">Partner Banks</h3>
            <div className="logos-grid">
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/hdfc-bank.png" alt="HDFC Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/axis-bank.png" alt="Axis Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/icici-bank.png" alt="ICICI Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/sbi-bank.png" alt="SBI" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/kotak-bank.png" alt="Kotak Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/yes-bank.png" alt="YES Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/federal-bank.png" alt="Federal Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/idbi-bank.png" alt="IDBI Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/iob-bank.png" alt="Indian Overseas Bank" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/bom-bank.png" alt="Bank of Maharashtra" className="partner-logo" />
              </div>
            </div>
          </div>

          {/* NBFCs Section */}
          <div className="partners-category">
            <h3 className="category-title">Partner NBFCs (Non-Banking Finance Companies)</h3>
            <div className="logos-grid">
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/tata-capital.png" alt="Tata Capital" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/lt-finance.png" alt="L&T Finance" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/bajaj-finserv.png" alt="Bajaj Finserv" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/hdb-financial.png" alt="HDB Financial" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/aditya-birla.png" alt="Aditya Birla Finance" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/muthoot-finance.png" alt="Muthoot Finance" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/shriram-finance.png" alt="Shriram Finance" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/mahindra-finance.png" alt="Mahindra Finance" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/icici-securities.png" alt="ICICI Securities" className="partner-logo" />
              </div>
              <div className="logo-placeholder fade-in">
                <img src="/images/partners/axis-finance.png" alt="Axis Finance" className="partner-logo" />
              </div>
            </div>
          </div>

          {/* Trust Message */}
          <div className="partners-message fade-in">
            <div className="message-card">
              <h4>🤝 Why Partner With Us?</h4>
              <ul>
                <li>✓ Zero Commission Fees</li>
                <li>✓ Transparent Process</li>
                <li>✓ Fastest Approvals</li>
                <li>✓ Multiple Options</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="content-wrapper">
          <div className="cta-content fade-in">
            <h2>Ready to Get Your Loan?</h2>
            <p>Join thousands of satisfied customers who have trusted us with their financial needs</p>
            <div className="cta-buttons">
              <Link to="/apply" className="cta-button primary">
                <FaBolt /> Apply Now
              </Link>
              <Link to="/emi-calculator" className="cta-button secondary">
                <FaCalculator /> Calculate EMI
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;